import React, { useState } from 'react';
import { useRippleEffect } from '../hooks/useRippleEffect';
import { API_CONFIG } from '../config/api';
import './Contact.css';

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  message: string;
}

export const Contact: React.FC = () => {
  const { ripples, createRipple } = useRippleEffect();
  
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  const [errors, setErrors] = useState<Partial<ContactFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [lastSubmitTime, setLastSubmitTime] = useState(0);

  // Validation rules
  const validateForm = (): boolean => {
    const newErrors: Partial<ContactFormData> = {};

    // Required field validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }
    // Phone validation (optional field)
    // eslint-disable-next-line no-useless-escape
    if (formData.phone.trim() && !/^[\+]?[\s\-\(\)]?\d{1,4}[\s\-\(\)]?[\d\s\-\(\)]{7,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name as keyof ContactFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmitTime < API_CONFIG.MIN_SUBMIT_INTERVAL) {
      setErrors({ message: 'Please wait a moment before submitting again.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setLastSubmitTime(now);

    try {
      console.log('Environment:', API_CONFIG.ENVIRONMENT);
      console.log('Submitting to:', API_CONFIG.CONTACT_FORM_ENDPOINT);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.REQUEST_TIMEOUT);
      
      const response = await fetch(API_CONFIG.CONTACT_FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const result = await response.json();
      console.log('API Response:', result);

      if (response.ok && result.success) {
        setSubmitStatus('success');
        // Reset form after successful submission
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          phone: '',
          message: ''
        });
        setErrors({});
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        throw new Error(result.error || `Server responded with status ${response.status}`);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      
      let errorMessage = 'Failed to submit your message. Please try again.';
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = 'Request timed out. Please check your connection and try again.';
        } else if (error.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message.includes('spam')) {
          errorMessage = 'Your message was flagged as spam. Please revise and try again.';
        } else if (error.message.includes('email')) {
          errorMessage = 'Please use a valid business email address.';
        }
      }
      
      setSubmitStatus('error');
      setErrors({ message: errorMessage });
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact" onClick={createRipple}>
      <div className="contact-container">
        <div className="contact-header">
          <h2 className="section-title">
            Get in <span className="title-highlight">Touch</span>
          </h2>
          <p className="section-description">
            Ready to transform your customer discovery process? Let's discuss how <span className="spotton-brand-text">Spott-on</span> can help your business grow.
          </p>
        </div>

        <div className="contact-content">
          <div className="contact-info">
            <div className="info-card">
              <div className="info-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#3ab7c3" strokeWidth="2"/>
                  <polyline points="22,6 12,13 2,6" stroke="#3ab7c3" strokeWidth="2"/>
                </svg>
              </div>
              <div className="info-content">
                <h3>Email Us</h3>
                <p>contact@intelyx.ai</p>
              </div>
            </div>
          </div>

          <div className="contact-form-container">
            {submitStatus === 'success' && (
              <div className="success-message">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div>
                  <h3>Thank You!</h3>
                  <p>Your message has been sent successfully. We'll get back to you within 24 hours.</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="error-message">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div>
                  <h3>Something went wrong</h3>
                  <p>Sorry, there was an error sending your message. Please try again or contact us directly.</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={errors.firstName ? 'error' : ''}
                    disabled={isSubmitting}
                    placeholder="Enter your first name"
                  />
                  {errors.firstName && <span className="field-error">{errors.firstName}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={errors.lastName ? 'error' : ''}
                    disabled={isSubmitting}
                    placeholder="Enter your last name"
                  />
                  {errors.lastName && <span className="field-error">{errors.lastName}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                  disabled={isSubmitting}
                  placeholder="Enter your email address"
                />
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="company">Company *</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className={errors.company ? 'error' : ''}
                  disabled={isSubmitting}
                  placeholder="Enter your company name"
                />
                {errors.company && <span className="field-error">{errors.company}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={errors.phone ? 'error' : ''}
                  disabled={isSubmitting}
                  placeholder="Enter your phone number (optional)"
                />
                {errors.phone && <span className="field-error">{errors.phone}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className={errors.message ? 'error' : ''}
                  disabled={isSubmitting}
                  placeholder="Tell us about your company or how we can help you..."
                  rows={4}
                />
                {errors.message && <span className="field-error">{errors.message}</span>}
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner"></div>
                      Sending Message...
                    </>
                  ) : (
                    <>
                      Send Message
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M4 10h12m-6-6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Ripple effects */}
      {ripples.map(ripple => (
        <div
          key={ripple.id}
          className="section-ripple"
          style={{
            left: ripple.x,
            top: ripple.y,
          }}
        >
          <div className="ripple-ring ring-1"></div>
          <div className="ripple-ring ring-2"></div>
          <div className="ripple-ring ring-3"></div>
        </div>
      ))}
    </section>
  );
};